'use strict';

const React = require('react');
const PropTypes = require('prop-types');
const classnames = require('classnames');

const { noop } = require('../../../mixins/commonjs/basicUtils');
const SwipeModel = require('../models/SwipeModel');
const BaseCarousel = require('../base/BaseCarousel');
const DotCarousel = require('./DotCarousel');

/**
 * Represents a carousel with that the user can swipe through on a touch device.
 * @class
 */
class SwipeCarousel extends React.Component {
    constructor(props) {
        super(props);

        // Dummy state. State is held in the `_model` but this is necessary to force a re-render when the model updates.
        this.state = {
            modelState: {},
        };
    }

    componentWillMount() {
        this._model = new SwipeModel({
            itemsPerPage: this.props.itemsPerPage,
            itemWidth: this.props.itemWidth,
            isCentered: this.props.isCentered,
            itemCount: React.Children.count(this.props.children),
            onPageChange: this.props.onPageChange,
            startingPage: this.props.startingPage,
            // Force a re-render if the model changes.
            onStateChange: modelState => this.setState({ modelState }),
        });

        // Set the initial model state. This is probably unnecessary but put here to avoid issues if state gets used
        // in `render` in the future.
        this.setState({ modelState: this._model.getState() });
    }

    /**
     * Imperatively set the page from outside the component, e.g. if the state of this carousel needs to be kept
     * constistent with another carousel.
     *
     * @param {number} page - The page to set.
     * @param {object} model - Model to update, needed for components where this method gets passed.
     * @param {function} callback
     */
    setPage(page, model = this._model, callback) {
        // Update the model which will in turn trigger an update to the view state and force a re-render.
        if (callback) {
            callback();
        }

        model.jumpToPage(page);
    }

    enableSwipe() {
        this._model.enableSwipe();
    }

    disableSwipe() {
        this._model.disableSwipe();
    }

    /**
     * Method to create a basic carousel which then can be shown or wrapped by other components.
     *
     * @returns {object} - JSX of a carousel.
     */
    _createCarousel() {
        const { wrapper, list, item } = this.props.classNames;

        const wrapperClass = classnames({
            ['swipe-carousel-wrapper']: true,
            [wrapper]: wrapper,
        });
        const listClass = classnames({
            ['swipe-carousel-list']: true,
            [list]: list,
        });
        const itemClass = classnames({
            ['swipe-carousel-item']: true,
            [item]: item,
        });

        return (
            <div className={wrapperClass}>
                <BaseCarousel model={this._model} classNames={{ list: listClass, item: itemClass }}>
                    {this.props.children}
                </BaseCarousel>
            </div>
        );
    }

    render() {
        if (this.props.useDotsNav) {
            return (
                <DotCarousel
                    classNames={this.props.classNames}
                    createCarousel={() => this._createCarousel()}
                    setPage={this.setPage}
                    model={this._model}
                    onDotClick={this.props.onDotClick}
                    dotsColorType={this.props.dotsColorType}
                />
            );
        }

        return this._createCarousel();
    }
}

SwipeCarousel.propTypes = {
    onPageChange: PropTypes.func,
    startingPage: PropTypes.number,
    // Change the number of items to display per carousel page.
    itemsPerPage: PropTypes.number,
    itemWidth: PropTypes.number,
    isCentered: PropTypes.bool,
    classNames: PropTypes.object,
    children: PropTypes.node,
    useDotsNav: PropTypes.bool,
    dotsColorType: PropTypes.oneOf(['buyer', 'trade']),
    onDotClick: PropTypes.func.isRequired,
};

SwipeCarousel.defaultProps = {
    itemsPerPage: 1,
    onPageChange: noop,
    onDotClick: noop,
    startingPage: 0,
    classNames: {},
};

module.exports = SwipeCarousel;



// WEBPACK FOOTER //
// ./assets/js/global/components/carousel/views/SwipeCarousel.jsx